import type { CoreLogger } from '@elara/core/logger';

import type { MessagePart, TextPart, WidgetPart } from '#schemas/message-schemas.js';
import type { RecommendationType } from '#schemas/widget-schemas.js';

import { isTextPart, isWidgetPart } from '#schemas/message-schemas.js';
import { WIDGET_MARKER_OPEN, WIDGET_MARKER_PATTERN } from '#schemas/widget-schemas.js';

function isPartialMarker(tail: string): boolean {
  if (tail.length <= WIDGET_MARKER_OPEN.length) return WIDGET_MARKER_OPEN.startsWith(tail);
  if (!tail.startsWith(WIDGET_MARKER_OPEN)) return false;
  return /^[^\]\s]*\]?$/u.test(tail.slice(WIDGET_MARKER_OPEN.length));
}

function partialMarkerStart(text: string): number {
  let position = text.lastIndexOf('[');
  let start = -1;
  for (let attempt = 0; attempt < 2 && position !== -1; attempt++) {
    if (isPartialMarker(text.slice(position))) start = position;
    position = position > 0 ? text.lastIndexOf('[', position - 1) : -1;
  }
  return start;
}

function markerBaseId(marker: string): string | undefined {
  const inner = marker.slice(WIDGET_MARKER_OPEN.length, -2);
  const cut = inner.lastIndexOf('-');
  return cut > 0 ? inner.slice(0, cut) : undefined;
}

function markerOf(part: WidgetPart): string | undefined {
  const { marker } = part.widget.payload as { marker?: unknown };
  return typeof marker === 'string' && marker.length > 0 ? marker : undefined;
}

/**
 * Every entity a recommendation widget would card, in order, de-duplicated within itself.
 *
 * A carousel names its set in `referenceIds` and need not repeat the first one in `referenceId`, so
 * reading either field alone under-reports what the card will show.
 */
function widgetEntities(part: WidgetPart): string[] {
  if (part.widget.type !== 'showRecommendationWidget') return [];
  const data = part.widget.payload.data as { referenceId?: unknown; referenceIds?: unknown };
  const ids = Array.isArray(data.referenceIds)
    ? data.referenceIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
    : [];
  if (ids.length > 0) return [...new Set(ids)];
  return typeof data.referenceId === 'string' && data.referenceId.length > 0 ? [data.referenceId] : [];
}

/** The same widget over a subset of its entities. `referenceId` follows only if the caller sent one. */
function withEntities(part: WidgetPart, ids: string[]): WidgetPart {
  const data = part.widget.payload.data as Record<string, unknown>;
  return {
    ...part,
    widget: {
      ...part.widget,
      payload: {
        ...part.widget.payload,
        data: {
          ...data,
          referenceIds: ids,
          ...(typeof data['referenceId'] === 'string' ? { referenceId: ids[0] } : {}),
        },
      },
    },
  };
}

function withoutMarker(part: WidgetPart): WidgetPart {
  const { marker: _marker, ...payload } = part.widget.payload as WidgetPart['widget']['payload'] & { marker?: unknown };
  return { ...part, widget: { ...part.widget, payload } };
}

export class WidgetMarkerInterleaver {
  #pendingText = '';
  #pendingMetadata: Record<string, unknown> | undefined;
  readonly #withheld = new Map<string, WidgetPart>();
  readonly #placed = new Set<string>();
  readonly #orphanMarkers = new Set<string>();
  readonly #materialized = new Set<string>();
  readonly #narrowed: Array<{ marker: string; dropped: string[]; kept: string[] }> = [];
  readonly #cardedAlone = new Set<string>();
  readonly #cardedInSet = new Set<string>();
  readonly #seenEntities = new Set<string>();
  #resolveRecommendationType: ((entityId: string) => RecommendationType | undefined) | undefined;

  /**
   * Lets an improvised marker become a card. Gemini sometimes ends a turn having written
   * `[[widget:<entityId>]]` for ids the data tools just returned WITHOUT calling the widget tool,
   * so no card exists to place. A real marker always carries a `-<nonce>` the tool minted, so a
   * marker that resolves to a bare entity id is unambiguously improvised and safe to rebuild.
   */
  setRecommendationTypeResolver(resolve: (entityId: string) => RecommendationType | undefined): void {
    this.#resolveRecommendationType = resolve;
  }

  push(part: MessagePart): MessagePart[] {
    if (isTextPart(part) && !part.temporary) return this.#pushText(part);

    const out = this.#drainText();
    if (isWidgetPart(part)) {
      const marker = markerOf(part);
      if (marker !== undefined && !this.#placed.has(marker) && !this.#withheld.has(marker)) {
        const staged = this.#dedupe(marker, part);
        if (staged === undefined) return out;
        if (this.#orphanMarkers.delete(marker)) {
          this.#placed.add(marker);
          out.push(withoutMarker(staged));
          return out;
        }
        this.#withheld.set(marker, staged);
        return out;
      }
    }
    out.push(part);
    return out;
  }

  flush(logger?: CoreLogger): MessagePart[] {
    const out = this.#drainText();
    if (logger && this.#materialized.size > 0) {
      logger.warn(
        { materialized: [...this.#materialized] },
        '[WidgetMarkerInterleaver] Rebuilt cards for markers the model wrote without calling the widget tool',
      );
    }
    if (logger && this.#narrowed.length > 0) {
      logger.warn(
        { narrowed: this.#narrowed.map((entry) => ({ ...entry })) },
        '[WidgetMarkerInterleaver] Trimmed entities from a set that an earlier set had already carded',
      );
    }
    if (logger && (this.#withheld.size > 0 || this.#orphanMarkers.size > 0)) {
      logger.warn(
        {
          stranded: [...this.#withheld.values()].map((part) => ({ type: part.widget.type, data: part.widget.payload.data })),
          orphanMarkers: [...this.#orphanMarkers],
        },
        '[WidgetMarkerInterleaver] Stream ended with widgets never marked or markers never backed by a widget call',
      );
    }
    for (const part of this.#withheld.values()) out.push(withoutMarker(part));
    this.reset();
    return out;
  }

  reset(): void {
    this.#pendingText = '';
    this.#pendingMetadata = undefined;
    this.#withheld.clear();
    this.#placed.clear();
    this.#orphanMarkers.clear();
    this.#materialized.clear();
    this.#narrowed.length = 0;
    this.#cardedAlone.clear();
    this.#cardedInSet.clear();
    this.#seenEntities.clear();
  }

  #pushText(part: TextPart): MessagePart[] {
    const hadPending = this.#pendingText.length > 0;
    this.#pendingText += part.text;
    if (part.metadata) this.#pendingMetadata = part.metadata;

    const out: MessagePart[] = [];
    let matched = false;
    for (let match = WIDGET_MARKER_PATTERN.exec(this.#pendingText); match; match = WIDGET_MARKER_PATTERN.exec(this.#pendingText)) {
      matched = true;
      this.#emitText(out, this.#pendingText.slice(0, match.index));
      this.#pendingText = this.#pendingText.slice(match.index + match[0].length);
      const marker = match[0];
      const widget = this.#withheld.get(marker);
      if (widget) {
        this.#withheld.delete(marker);
        this.#placed.add(marker);
        out.push(withoutMarker(widget));
      } else if (!this.#placed.has(marker)) {
        const rebuilt = this.#materialize(marker);
        if (rebuilt) {
          this.#placed.add(marker);
          out.push(rebuilt);
        } else {
          this.#orphanMarkers.add(marker);
        }
      }
    }

    const holdFrom = partialMarkerStart(this.#pendingText);
    if (!hadPending && !matched && holdFrom === -1) {
      this.#pendingText = '';
      return [part];
    }
    if (holdFrom === -1) {
      this.#emitText(out, this.#pendingText);
      this.#pendingText = '';
    } else {
      this.#emitText(out, this.#pendingText.slice(0, holdFrom));
      this.#pendingText = this.#pendingText.slice(holdFrom);
    }
    return out;
  }

  /**
   * At most one card per entity PER SHAPE: a hotel may headline its own card and also sit in a set
   * the model is comparing, because those say different things — but not two solo cards, and not two
   * carousels. So the seen-set is chosen by the widget's shape rather than shared between them.
   *
   * A set that repeats only SOME of an earlier set's entities is narrowed to the rest rather than
   * dropped. Dropping it whole was the bug this replaced: a five-hotel carousel sharing one entity
   * with an earlier one lost the other four, silently, under prose still comparing five. Narrowing
   * to nothing IS the drop, so this adds no new way for a card to vanish.
   *
   * `#seenEntities` keeps the union, for `#materialize` alone — a card GUESSED from a stray marker
   * should not land on top of one the model actually asked for, in either shape.
   */
  #dedupe(marker: string, part: WidgetPart): WidgetPart | undefined {
    const entities = widgetEntities(part);
    if (entities.length === 0) return part;

    const carded = entities.length > 1 ? this.#cardedInSet : this.#cardedAlone;
    const unseen = entities.filter((id) => !carded.has(id));
    if (unseen.length === 0) return;
    for (const id of unseen) {
      carded.add(id);
      this.#seenEntities.add(id);
    }
    if (unseen.length === entities.length) return part;

    this.#narrowed.push({ marker, dropped: entities.filter((id) => !unseen.includes(id)), kept: unseen });
    return withEntities(part, unseen);
  }

  #materialize(marker: string): WidgetPart | undefined {
    const resolve = this.#resolveRecommendationType;
    if (!resolve) return;
    const inner = marker.slice(WIDGET_MARKER_OPEN.length, -2);
    const candidates = [inner, markerBaseId(marker)];
    let entityId: string | undefined;
    let type: RecommendationType | undefined;
    for (const candidate of candidates) {
      if (candidate === undefined || candidate.length === 0) continue;
      const resolved = resolve(candidate);
      if (resolved !== undefined) {
        entityId = candidate;
        type = resolved;
        break;
      }
    }
    if (entityId === undefined || type === undefined || this.#seenEntities.has(entityId)) return;
    this.#seenEntities.add(entityId);
    // What this builds IS a solo card, so it takes that entity's solo slot: a real widget for the
    // same entity arriving later is then dropped as the repeat it is. Without this the two never
    // met — the real one is withheld under a marker the model never wrote, so it surfaces at the
    // bottom of the reply and the entity is carded twice.
    this.#cardedAlone.add(entityId);
    this.#materialized.add(entityId);
    return { widget: { type: 'showRecommendationWidget', payload: { data: { type, referenceId: entityId } } } };
  }

  #drainText(): MessagePart[] {
    const out: MessagePart[] = [];
    this.#emitText(out, this.#pendingText);
    this.#pendingText = '';
    return out;
  }

  #emitText(out: MessagePart[], text: string): void {
    if (text.length === 0) return;
    out.push(this.#pendingMetadata ? { text, metadata: this.#pendingMetadata } : { text });
  }
}
